import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Stack,
  Select,
  useTheme,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { PHONENUMBER_REGEX } from 'src/utils/regex';
import { phoneNumberSplit } from 'src/utils/phoneNumberSplit';

import { styles } from '../styles';
import countries from '../../../locale/phoneCode';

export default function UserInfoForm({ modifyUser }) {
  const { i18n, t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { user } = useAuth();
  const { showToast, hideToast } = useToast();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: {
      id: user.id,
      givenName: user.givenName,
      familyName: user.familyName,
      username: user.username,
      countryCode: phoneNumberSplit(user?.phoneNumber).partOne,
      phoneNumber: phoneNumberSplit(user?.phoneNumber).partTwo,
      email: user.email,
      avatar: user.avatar,
    },
  });

  useMemo(() => {
    const defaultValues = {
      id: user.id,
      givenName: user.givenName,
      familyName: user.familyName,
      username: user.username,
      countryCode: phoneNumberSplit(user?.phoneNumber).partOne,
      phoneNumber: phoneNumberSplit(user?.phoneNumber).partTwo,
      email: user.email,
      avatar: user.avatar,
    };
    reset({ ...defaultValues });
  }, [user, reset]);

  useEffect(() => {
    countries.sort((a, b) => a[i18n.language].localeCompare(b[i18n.language]));
  }, [i18n.language]);

  const handleCancelChanges = () => {
    reset();
  };

  const onSubmit = async (data) => {
    data.phoneNumber = `(${data.countryCode}) ${data.phoneNumber}`;
    setLoading(true);
    try {
      await modifyUser(data);
      showToast({
        message: t('successMessages.successUpdate'),
        severity: 'success',
        props: { hideToast },
      });
    } catch (error) {
      showToast({
        message: t('errorMessages.couldntUpdateInfo'),
        severity: 'error',
        props: { hideToast },
      });
    } finally {
      setLoading(false);
      reset();
    }
  };

  return (
    <Box>
      <Card
        sx={{
          ...styles.formCard,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} key={user.id}>
          <Stack mb={3} direction="row" spacing={3}>
            <TextField
              fullWidth
              label={t('attributes.firstName')}
              error={!!errors.givenName}
              helperText={errors.givenName?.message || ''}
              {...register('givenName', {
                required: t('formControl.enterName'),
                minLength: { value: 3, message: t('formControl.minName') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />
            <TextField
              fullWidth
              label={t('attributes.lastName')}
              error={!!errors.familyName}
              helperText={errors.familyName?.message || ''}
              {...register('familyName', {
                required: t('formControl.enterLastName'),
                minLength: { value: 3, message: t('formControl.minLastName') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Stack>
          <Stack spacing={3}>
            <TextField
              label={t('attributes.userName')}
              error={!!errors.username}
              helperText={errors.username?.message || ''}
              {...register('username', {
                required: t('formControl.enterName'),
                minLength: { value: 3, message: t('formControl.minName') },
              })}
              size={isSmallScreen ? 'small' : 'medium'}
            />
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ width: isSmallScreen ? '50%' : '20%' }}>
                <InputLabel id="country-code-label">{t('attributes.country')}</InputLabel>
                <Select
                  labelId="country-code-label"
                  label={t('attributes.country')}
                  value={watch('countryCode') || '+39'}
                  {...register('countryCode', {
                    required: t('formControl.selectCountryCode'),
                  })}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        overflowY: 'auto',
                        // Styles the dropdown's vertical scrollbar cleanly
                        '&::-webkit-scrollbar': {
                          width: '5px',
                          height: '5px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.15)'
                            : 'rgba(0, 0, 0, 0.15)',
                          borderRadius: '10px',
                          '&:hover': {
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.3)'
                              : 'rgba(0, 0, 0, 0.3)',
                          },
                        },
                      },
                    },
                  }}
                  size={isSmallScreen ? 'small' : 'medium'}
                >
                  {countries.map((country, index) => (
                    <MenuItem key={index} value={country.code}>
                      {`${country[i18n.language]} (${country.code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label={t('attributes.phoneNumber')}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message || ''}
                {...register('phoneNumber', {
                  required: t('formControl.enterPhoneNumber'),
                  pattern: {
                    value: PHONENUMBER_REGEX,
                    message: t('formControl.invalidPhoneNumber'),
                  },
                })}
                size={isSmallScreen ? 'small' : 'medium'}
                sx={{ width: '80%' }}
              />
            </Stack>
            <TextField
              disabled
              label={t('pages.usersPage.form.emailAdress')}
              error={!!errors.email}
              helperText={errors.email?.message || ''}
              {...register('email')}
              size={isSmallScreen ? 'small' : 'medium'}
            />
            <Stack direction="row" spacing={1}>
              <LoadingButton
                fullWidth
                variant="contained"
                color="error"
                onClick={handleCancelChanges}
                disabled={!isDirty}
              >
                {t('buttons.cancelChanges')}
              </LoadingButton>
              <LoadingButton
                fullWidth
                loading={loading}
                type="submit"
                variant="contained"
                color="success"
                disabled={!isValid || !isDirty}
              >
                {t('buttons.update')}
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </Card>
    </Box>
  );
}

UserInfoForm.propTypes = {
  modifyUser: PropTypes.func.isRequired,
};
